/**
 * nh-pdp-thumbs
 *
 * Thumbnail rail for the GRID (editorial stack) product media gallery.
 * See snippets/nh-pdp-thumbs.liquid for why it exists.
 *
 * NOTE ON THE NAME: this is NOT assets/nh-media-rail.js. That file owns
 * <nh-media-rail>, the horizontal scroll-snap carousel used by the High
 * Jewelry piece and salon pages. Two custom elements cannot share a tag
 * name — whichever registers first wins and the other silently dies — so
 * this component is deliberately named differently.
 *
 * The rail is an index onto the existing stack, not a replacement for it.
 * Clicking a thumbnail scrolls the corresponding media into view; an
 * IntersectionObserver keeps the active thumbnail in step as the customer
 * scrolls the stack by hand. Nothing swaps, nothing is hidden, so the
 * editorial scroll behaviour is unchanged.
 */

class NHPdpThumbs extends HTMLElement {
  connectedCallback() {
    // The rail renders once per gallery and sits as a sibling of the grid.
    this.gallery = this.closest('media-gallery');
    if (!this.gallery) return;

    this.slides = Array.from(this.gallery.querySelectorAll('.media-gallery__grid > li'));
    this.thumbs = Array.from(this.querySelectorAll('[data-nh-thumbs-target]'));
    this.currentEl = this.querySelector('[data-nh-thumbs-current]');

    // Guard: if the grid is not the presentation in play (carousel uses the
    // theme's own slideshow controls) there is nothing to index.
    if (this.slides.length < 2 || this.thumbs.length === 0) {
      this.hidden = true;
      return;
    }

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    this.observe();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.observer) this.observer.disconnect();
  }

  onClick(event) {
    const thumb = event.target.closest('[data-nh-thumbs-target]');
    if (!thumb) return;

    const index = Number(thumb.dataset.nhThumbsTarget) - 1;
    const slide = this.slides[index];
    if (!slide) return;

    // 'start' plus the document's scroll-padding-top (96px on this theme)
    // keeps the media clear of the top of the viewport.
    slide.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });

    this.setActive(index);
  }

  /**
   * Highlight whichever media occupies most of the viewport. Using the
   * largest intersection ratio rather than the first intersecting entry
   * matters for the two-column rows in the editorial rhythm, where two
   * media are on screen at once.
   */
  observe() {
    const ratios = new Array(this.slides.length).fill(0);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const i = this.slides.indexOf(entry.target);
          if (i > -1) ratios[i] = entry.isIntersecting ? entry.intersectionRatio : 0;
        }

        let best = -1;
        let bestRatio = 0;
        ratios.forEach((r, i) => {
          if (r > bestRatio) {
            bestRatio = r;
            best = i;
          }
        });

        if (best > -1) this.setActive(best);
      },
      {
        // Ignore the top and bottom slices when deciding which media is
        // "current", so the active thumb changes at a natural point.
        rootMargin: '-15% 0px -35% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  setActive(index) {
    if (this.activeIndex === index) return;
    this.activeIndex = index;

    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    if (this.currentEl) this.currentEl.textContent = String(index + 1);

    // Keep the active thumbnail in view when the rail itself scrolls.
    const active = this.thumbs[index];
    if (active && this.scrollHeight > this.clientHeight) {
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;
      if (top < this.scrollTop || bottom > this.scrollTop + this.clientHeight) {
        active.scrollIntoView({ block: 'nearest' });
      }
    }
  }
}

if (!customElements.get('nh-pdp-thumbs')) {
  customElements.define('nh-pdp-thumbs', NHPdpThumbs);
}
